import { useState, ChangeEvent } from 'react'
import { Button, Col, Form, FormControl, FormSelect, Row } from 'react-bootstrap'
import { FilePersonFill } from 'react-bootstrap-icons'
import { t } from '../locale'
import { ChangeEvt, CompanyForm, PersonForm } from '../types/stammdaten.type'
import { useOutsideClick } from '../hooks/useOutsideClick'
import KDayPicker from '../components/KDayPicker'

type UnthernamenProps = {
  form: CompanyForm | PersonForm
  handleChangeLocal: (e: ChangeEvt) => void
  handleDateInputChange: (e: ChangeEvent<HTMLInputElement>) => void
  history: (path: string, label: string, options?: { value: any; label: string }[]) => void
  options: any
  banks: any
  handleDateChange: (id: string, date: Date | null) => void
  handleAddBank: () => void
  handleRemoveBank?: (index: number) => void
  required: any
  availableBanks?: any[]
}

export default function StammdatenBank({
  form,
  handleChangeLocal,
  handleDateInputChange,
  history,
  options,
  banks,
  handleDateChange,
  handleAddBank,
  handleRemoveBank,
  required,
  availableBanks = [],
}: UnthernamenProps) {
  const [datePickerValidSince, setDatePickerValidSince] = useState<number | null>(null)
  const [datePickerValidTo, setDatePickerValidTo] = useState<number | null>(null)

  useOutsideClick(() => {
    setDatePickerValidSince(null)
    setDatePickerValidTo(null)
  }, 'bank-date-area')
  return (
    <>
      <div id='bank-data' className='mb-4'>
        {form.bank.map((bank, idx) => (
          <div key={idx} className='mb-4'>
            <div className='bg-gray-200 p-2 d-flex align-items-center justify-content-between mb-3'>
              <span className='d-flex align-items-center'>
                <FilePersonFill />
                &nbsp;{t('bank')} {banks.length > 1 ? `#${idx + 1}` : ''}
              </span>
              <p
                className='text-danger small'
                onClick={e => {
                  e.preventDefault()
                  handleRemoveBank?.(idx)
                }}
              >
                {t('remove')}
              </p>
            </div>

            {/* Gültig: von / bis */}
            <Row className='mt-2'>
              <Col className='col-sm-2'>
                <label
                  className={`form-control-label ${required.bank.valid_since ? 'required' : ''}`}
                >
                  {t('gültig')}
                </label>
              </Col>

              <Col className='col-sm-6'>
                <div className='d-flex'>
                  {/* FROM */}
                  <div className='me-4 position-relative bank-date-area'>
                    <div className='d-flex align-items-center mb-1'>
                      <span className='text-muted small me-1'>{t('seit')}</span>
                      <button
                        type='button'
                        className='btn btn-link btn-sm p-0'
                        onClick={() => history(`stammdaten.bank.${idx}.valid_since`, t('gültig'))}
                      >
                        <i className='fa fa-history text-dark' />
                      </button>
                    </div>

                    <Form.Control
                      id={`bank.${idx}.valid_since`}
                      type='text'
                      name='gültig'
                      className='form-control-sm'
                      value={bank.valid_since || ''}
                      onClick={() =>
                        setDatePickerValidSince(datePickerValidSince === idx ? null : idx)
                      }
                      onChange={handleDateInputChange}
                      placeholder='DD.MM.YYYY'
                      inputMode='numeric'
                      maxLength={10}
                      required={required.bank.valid_since}
                    />
                    <KDayPicker
                      open={datePickerValidSince === idx}
                      value={bank.valid_since}
                      required={required.bank.valid_since}
                      onSelect={d => {
                        handleDateChange(`bank.${idx}.valid_since`, d)
                        setDatePickerValidSince(null)
                      }}
                    />
                  </div>

                  {/* UNTIL */}
                  <div className='position-relative bank-date-area'>
                    <div className='d-flex align-items-center mb-1'>
                      <span className='text-muted small me-1'>{t('until')}</span>
                      <button
                        type='button'
                        className='btn btn-link btn-sm p-0'
                        onClick={() => history(`stammdaten.bank.${idx}.valid_to`, t('until'))}
                      >
                        <i className='fa fa-history text-dark' />
                      </button>
                    </div>

                    <Form.Control
                      id={`bank.${idx}.valid_to`}
                      type='text'
                      name='bis'
                      className='form-control-sm'
                      value={bank.valid_to || ''}
                      onClick={() => setDatePickerValidTo(datePickerValidTo === idx ? null : idx)}
                      onChange={handleDateInputChange}
                      placeholder='DD.MM.YYYY'
                      inputMode='numeric'
                      maxLength={10}
                      required={required.bank.valid_to}
                    />

                    <KDayPicker
                      open={datePickerValidTo === idx}
                      value={bank.valid_to}
                      required={required.bank.valid_to}
                      onSelect={d => {
                        handleDateChange(`bank.${idx}.valid_to`, d)
                        setDatePickerValidTo(null)
                      }}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <hr />

            {/* Country */}
            <Row className='mt-4'>
              <Col className='col-sm-2'>
                <label className={`form-control-label required`}>{t('country')}</label>
              </Col>
              <Col>
                <FormSelect
                  name={t('country')}
                  className='w-25 d-inline'
                  id={`bank.${idx}.country`}
                  value={bank.country}
                  required={true}
                  onChange={handleChangeLocal}
                >
                  <option value=''>--</option>
                  {options.map((option: any) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </FormSelect>
                <button
                  type='button'
                  className='btn btn-link btn-sm'
                  onClick={() => history(`stammdaten.bank.${idx}.country`, t('country'), options)}
                >
                  <i className='fa fa-history text-dark' />
                </button>
              </Col>
            </Row>

            <Row className='mt-4'>
              <Col className='col-sm-2'>
                <label className={`form-control-label required`}>{t('bank')}</label>
              </Col>
              <Col>
                <FormControl
                  name={t('bank')}
                  className='w-50 d-inline bg-info2'
                  type='text'
                  placeholder='BLZ / BIC / Name / Place'
                  id={`bank.${idx}.bank_name`}
                  value={bank.bank_name}
                  onChange={handleChangeLocal}
                  required={true}
                  list={`bank-options-${idx}`}
                  autoComplete='off'
                  disabled={!bank.country}
                  style={
                    !bank.country
                      ? {
                          backgroundColor: '#e9ecef',
                          opacity: 0.65,
                          cursor: 'not-allowed',
                          pointerEvents: 'none',
                        }
                      : {}
                  }
                />
                <datalist id={`bank-options-${idx}`}>
                  {availableBanks.map((availableBank: any, bankIdx: number) => (
                    <option
                      key={bankIdx}
                      value={`${availableBank.name} - ${availableBank.bic || availableBank.bank_code || ''}`}
                    >
                      {availableBank.city && ` - ${availableBank.city}`}
                    </option>
                  ))}
                </datalist>
                <button
                  type='button'
                  className='btn btn-link btn-sm'
                  onClick={() =>
                    history(`stammdaten.bank.${idx}.bank_name`, t('BLZ / BIC / Name / Place'))
                  }
                >
                  <i className='fa fa-history text-dark' />
                </button>
              </Col>
            </Row>

            {/* IBAN */}
            <Row className='mt-4'>
              <Col className='col-sm-2'>
                <label className={`form-control-label required`}>{t('iban')}</label>
              </Col>
              <Col>
                <FormControl
                  name={t('iban')}
                  className='w-50 d-inline'
                  type='text'
                  id={`bank.${idx}.iban`}
                  value={bank.iban}
                  onChange={handleChangeLocal}
                  disabled={!bank.country}
                  style={
                    !bank.country
                      ? {
                          backgroundColor: '#e9ecef',
                          opacity: 0.65,
                          cursor: 'not-allowed',
                          pointerEvents: 'none',
                        }
                      : {}
                  }
                  required={true}
                />
                <button
                  type='button'
                  className='btn btn-link btn-sm'
                  onClick={() => history(`stammdaten.bank.${idx}.iban`, t('iban'))}
                >
                  <i className='fa fa-history text-dark' />
                </button>
              </Col>
            </Row>

            {/* Kontonummer */}
            <Row className='mt-4'>
              <Col className='col-sm-2'>
                <label
                  className={`form-control-label ${required.bank.account_number ? 'required' : ''}`}
                >
                  {t('kontonummer')}
                </label>
              </Col>
              <Col>
                <FormControl
                  name={t('kontonummer')}
                  className='w-50 d-inline'
                  type='text'
                  id={`bank.${idx}.account_number`}
                  value={bank.account_number}
                  onChange={handleChangeLocal}
                  required={required.bank.account_number}
                />
                <button
                  type='button'
                  className='btn btn-link btn-sm'
                  onClick={() => history(`stammdaten.bank.${idx}.account_number`, t('kontonummer'))}
                >
                  <i className='fa fa-history text-dark' />
                </button>
              </Col>
            </Row>

            {/* Kontoinhaber */}
            <Row className='mt-4'>
              <Col className='col-sm-2'>
                <label
                  className={`form-control-label ${required.bank.account_holder ? 'required' : ''}`}
                >
                  {t('kontoinhaber')}
                </label>
              </Col>
              <Col>
                <FormControl
                  name={t('kontoinhaber')}
                  className='w-50 d-inline'
                  type='text'
                  id={`bank.${idx}.account_holder`}
                  value={bank.account_holder}
                  onChange={handleChangeLocal}
                  required={required.bank.account_holder}
                />
                <button
                  type='button'
                  className='btn btn-link btn-sm'
                  onClick={() =>
                    history(`stammdaten.bank.${idx}.account_holder`, t('kontoinhaber'))
                  }
                >
                  <i className='fa fa-history text-dark' />
                </button>
              </Col>
            </Row>

            {/* Notiz */}
            <Row className='mt-4'>
              <Col className='col-sm-2'>
                <label className={`form-control-label ${required.bank.note ? 'required' : ''}`}>
                  {t('Notiz')}
                </label>
              </Col>
              <Col>
                <textarea
                  name={t('Notiz')}
                  className='w-50 d-inline form-control'
                  id={`bank.${idx}.note`}
                  value={bank.note}
                  onChange={handleChangeLocal}
                  required={required.bank.note}
                ></textarea>
                <button
                  type='button'
                  className='btn btn-link btn-sm'
                  onClick={() => history(`stammdaten.bank.${idx}.note`, t('notiz'))}
                >
                  <i className='fa fa-history text-dark' />
                </button>
              </Col>
            </Row>
          </div>
        ))}

        <hr />

        {/* Add (+) */}
        <Row className='mt-3'>
          <Col>
            <Button
              variant='success'
              size='sm'
              className='btn btn-light p-0 d-flex align-items-center justify-content-center'
              style={{
                width: '28px',
                height: '28px',
                border: '1px solid #ced4da',
                color: '#198754', // bootstrap green
                fontWeight: 600,
                lineHeight: 1,
              }}
              onClick={handleAddBank}
            >
              +
            </Button>
          </Col>
        </Row>
      </div>
    </>
  )
}
